/**
 * Company: SPHMedia
 * Description: [Hook for Brightcove video integration]
 */

import mediaController from "@helper/mediaController";
import type { VideoPlayerRef } from "@typings/Brightcove.d";
import { RefObject, useCallback, useEffect, useId, useRef } from "react";

type useBrightcoveVideoData = {
  videoRef: RefObject<VideoPlayerRef | null>;
  handleSuccess: (success: { ref: VideoPlayerRef }) => void;
};

type useBrightcoveVideoProps = {
  onEnded?: () => void;
  nextPlaylist?: () => void;
  onPlayerReady?: (player: VideoPlayerRef) => void;
};

/**
 * Custom React Hook: useBrightcoveVideoHook
 * Description: [Hook for managing Brightcove video state and lifecycle]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useBrightcoveVideo({
  onEnded,
  nextPlaylist,
  onPlayerReady,
}: useBrightcoveVideoProps): useBrightcoveVideoData {
  const videoRef = useRef<VideoPlayerRef | null>(null);
  const useRefId = useId();
  const playerRefId = useRef(`brightcove-ref-${useRefId.replace(/:/g, "")}`);

  const handleSuccess = useCallback(
    (success: { ref: VideoPlayerRef | null }) => {
      const player = success.ref;

      if (!player) return;

      const { onPlay, onPause } = mediaController.register({
        type: "brightcove",
        pause: () => {
          player.pause();
        },
        id: playerRefId.current,
      });

      const triggerOnPlay = () => {
        const isMuted = player.muted();
        const isPlaying = !player.paused?.();

        if (!isMuted && isPlaying) {
          onPlay();
        }
      };

      player.on("loadedmetadata", () => {
        triggerOnPlay();
      });

      player.on("volumechange", () => {
        triggerOnPlay();
      });

      player.on("play", () => {
        if (!player.muted()) {
          onPlay();
        }
      });

      player.on("pause", onPause);

      if (onEnded) {
        player.on("ended", () => {
          onEnded();
        });
      }

      const nextButton = player.el().querySelector(".vjs-next-button");
      const handleNextButtonClick = () => nextPlaylist?.();

      nextButton?.addEventListener("click", handleNextButtonClick);

      onPlayerReady?.(player);

      videoRef.current = player;

      player.on("dispose", () => {
        nextButton?.removeEventListener("click", handleNextButtonClick);
        onPause();
      });
    },
    [onEnded, nextPlaylist, onPlayerReady],
  );

  useEffect(() => {
    function handlePauseAllPlayers(e: CustomEvent<{ sourceId: string }>) {
      const player = videoRef.current;
      if (!player) return;

      // Ignore if this Brightcove is the source
      if (e.detail.sourceId === playerRefId.current) return;

      try {
        player.pause();
      } catch (err) {
        console.warn("[mediaController] Failed to pause Brightcove:", err);
      }
    }

    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );
    return () => {
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, []);

  return {
    videoRef,
    handleSuccess,
  };
}
